import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
import { FC, KeyboardEvent } from 'react';
import moment from 'moment';
import { Button, Form } from 'antd';
import PitchFormImage from '../../../assets/PitchFormImage.svg';
import editProfileInfo from '../../../assets/editAdminIcon.svg';
import { CalendarSvg } from '../../../components/Icons/CalendarIcon';
import { ClockIcon } from '../../../components/Icons/ClockIcon';
import stopImage from '../../../assets/stopImage.svg';
import { useManagePitchMutation, useSubmitPitchMutation } from '../../../api/tenders';
import { useSearchParams } from 'react-router-dom';
import { RequestNewSlot } from '../RequestNewSlot';
import { WithdrawModal } from '../WithdrawModal';
import { ParticipantI } from '../SupplierPitchFormSteps';
import { FloatInput } from '../../../components/Fields/FloatInput';
import { RequsetNewSlotSuccess } from '../success-screens/RequsetNewSlotSuccess';
import { Loader } from '../../../components/Loader/Loader';
import { PitchSlotConfirmedSuccess } from '../PitchSlotConfirmedSuccess';
import { ParticipationWithdrawnSuccess } from '../success-screens/ParticipationWithdrawnSuccess';

const statusCheckIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle
            cx="9.99922"
            cy="10"
            r="7.50312"
            stroke="#001965"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M7.03516 10.2826L8.84174 12.0892L8.83007 12.0775L12.9059 8.00167"
            stroke="#001965"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export interface ProposedPitchSlot {
    label: string;
    dateLabel: string;
    timeRange: string;
}

export interface NewPitchSlotsProposedProps {
    data: any;
}

/** Maps API participants (`full_name` or `name`) and pads to `representatives_count`. */
const mapParticipantsFromData = (pitchData: any): ParticipantI[] => {
    if (!pitchData) return [];
    const count = Number(pitchData.representatives_count) || 0;
    const raw = Array.isArray(pitchData.participants) ? pitchData.participants : [];
    const mapped = raw.map((elem: any) => ({
        title: elem?.title != null ? String(elem.title) : '',
        full_name: elem?.full_name != null ? String(elem.full_name) : elem?.name != null ? String(elem.name) : ''
    }));

    if (count <= 0) {
        return mapped;
    }
    return Array.from({ length: count }, (_, i) => mapped[i] ?? { title: '', full_name: '' });
};

export const NewPitchSlotsProposed: FC<NewPitchSlotsProposedProps> = ({ data }) => {
    const [searchParams] = useSearchParams();
    const signature = searchParams.get('signature');

    const [requestNewSlotModal, setRequestNewSlotModal] = useState(false);
    const [withdrawModal, setWithDrawModal] = useState(false);

    const [withdrawReason, setWithdrawReason] = useState('');
    const [newSlot, setNewSlot] = useState('');
    const [participantsData, setParticipantsData] = useState<ParticipantI[]>([]);
    const [errors, setErrors] = useState<number[]>([]);

    const [activeStep, setActiveStep] = useState(1);
    const [hasVisitedParticipantsStep, setHasVisitedParticipantsStep] = useState(false);

    useEffect(() => {
        setParticipantsData(mapParticipantsFromData(data));
    }, [data, data?.participants, data?.representatives_count]);

    const handleInputChange = (index: number, value: string, field: keyof ParticipantI) => {
        setParticipantsData((prev) => {
            const updated = [...prev];

            if (!updated[index]) {
                updated[index] = {
                    title: '',
                    full_name: ''
                };
            }

            updated[index] = {
                ...updated[index],
                [field]: value
            };

            return updated;
        });

        setErrors((prev) => prev.filter((i) => i !== index));
    };

    const [selectedProposedSlotIndex, setSelectedProposedSlotIndex] = useState(0);

    const [submitPitch, { isSuccess, isLoading }] = useSubmitPitchMutation();
    const [withdrawPitch, { isSuccess: isSuccessWithdraw, isLoading: isLoadingWithdraw }] = useManagePitchMutation();

    const slots = data?.slots ?? [];

    const chosenSlot = slots[selectedProposedSlotIndex];

    const { pitchConfirmedDateLabel, pitchConfirmedTimeRange } = useMemo(() => {
        if (!chosenSlot) {
            return { pitchConfirmedDateLabel: '', pitchConfirmedTimeRange: '' };
        }
        return {
            pitchConfirmedDateLabel: chosenSlot.date ? moment(chosenSlot.date).format('DD MMM YYYY') : '',
            pitchConfirmedTimeRange: [chosenSlot.time_from, chosenSlot.time_to].filter(Boolean).join(' - ')
        };
    }, [chosenSlot]);

    // const pitchSlot = data?.assigned_slot;
    const pitchSlot = slots[0];

    const buildPitchFormData = () => {
        const formData = new FormData();

        participantsData?.forEach((participant, index) => {
            formData.append(`participants[${index}][title]`, participant.title);
            formData.append(`participants[${index}][name]`, participant.full_name);
        });

        formData.append('status', newSlot ? 'slots_request' : 'submitted');

        if (newSlot) {
            formData.append('reason', newSlot);
            formData.append(`assigned_slot[date]`, pitchSlot.date);
            formData.append(`assigned_slot[time_from]`, pitchSlot.time_from);
            formData.append(`assigned_slot[time_to]`, pitchSlot.time_to);
        } else {
            slots.forEach((slot: any, index: number) => {
                formData.append(`slots[${index}][date]`, slot.date);
                formData.append(`slots[${index}][time_from]`, slot.time_from);
                formData.append(`slots[${index}][time_to]`, slot.time_to);
            });
            if (chosenSlot) {
                formData.append(`assigned_slot[date]`, chosenSlot.date);
                formData.append(`assigned_slot[time_from]`, chosenSlot.time_from);
                formData.append(`assigned_slot[time_to]`, chosenSlot.time_to);
            }
        }

        return formData;
    };

    const handleSubmit = () => {
        if (withdrawReason) {
            const formData = {
                signature: signature,
                data: {
                    reason: withdrawReason,
                    status: 'declined'
                }
            };
            withdrawPitch(formData);
        } else {
            const newErrors: number[] = [];

            const count = data?.representatives_count || 0;

            for (let i = 0; i < count; i++) {
                const participant = participantsData[i];

                if (!participant || !participant.title?.trim() || !participant.full_name?.trim()) {
                    newErrors.push(i);
                }
            }

            setErrors(newErrors);

            if (newErrors.length === 0) {
                submitPitch({
                    signature: signature,
                    data: buildPitchFormData()
                });
            }
        }
    };

    const onSlotKeyDown = (index: number) => (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedProposedSlotIndex(index);
        }
    };

    const hasSlots = slots.length > 0;

    const representativesCount = data?.representatives_count || 0;

    const participantsValid = useMemo(() => {
        if (representativesCount <= 0) {
            return true;
        }
        for (let i = 0; i < representativesCount; i++) {
            const p = participantsData[i];
            if (!p?.title?.trim() || !p?.full_name?.trim()) {
                return false;
            }
        }
        return true;
    }, [participantsData, representativesCount]);

    const participationFields = () => (
        <>
            <p className="supplier-pitch__description">Provide information about participants</p>
            {Array(data?.representatives_count)
                ?.fill(1)
                ?.map((_elem: unknown, index: number) => (
                    <Fragment key={index}>
                        <div className={`slot ${errors.includes(index) ? 'error' : ''}`} id="participants">
                            <div className="slot__title">Participant {index + 1}</div>
                            <Form className="slot__form">
                                <Form.Item className="input-wrapper">
                                    <FloatInput
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            handleInputChange(index, e.target.value, 'title')
                                        }
                                        floatLabel={'Title'}
                                        required
                                        className="input"
                                        maxLength={60}
                                        value={participantsData[index]?.title}
                                    />
                                </Form.Item>

                                <Form.Item className="input-wrapper">
                                    <FloatInput
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            handleInputChange(index, e.target.value, 'full_name')
                                        }
                                        floatLabel={'Full Name'}
                                        required
                                        className="input"
                                        maxLength={128}
                                        value={participantsData[index]?.full_name}
                                    />
                                </Form.Item>
                            </Form>
                        </div>
                        {errors.includes(index) && (
                            <div className="error-text" style={{ margin: '-15px 0 10px 0' }}>
                                {index === 0 ? 'These are required fields' : 'Please, specify Title and Full Name'}
                            </div>
                        )}
                    </Fragment>
                ))}
        </>
    );

    const participationSection = () => (
        <section key="participants" className="supplier-pitch__section">
            <div className="supplier-pitch__section-header">
                <span className="supplier-pitch__section-number">2</span>
                <h2 className="supplier-pitch__section-title">Participant Information</h2>
            </div>
            {participationFields()}
        </section>
    );

    const participationSectionPending = () => (
        <section key="participants" className="supplier-pitch__section">
            <div className="supplier-pitch__section-header">
                <span className="supplier-pitch__section-number inactive">2</span>
                <h2 className="supplier-pitch__section-title">Participant Information</h2>
            </div>
        </section>
    );

    const participationSectionCompleted = () => (
        <section key="participants" className="supplier-pitch__section">
            <div className="supplier-pitch__section-header">
                <span className="supplier-pitch__section-number inactive">2</span>
                <h2 className="supplier-pitch__section-title">Participant Information</h2>
            </div>
            <div className="supplier-pitch__status-bar">
                <div className="supplier-pitch__status-info">
                    <span className="supplier-pitch__status-icon">{statusCheckIcon}</span>
                    <span className="supplier-pitch__status-text">Participant info provided</span>
                </div>
                <button type="button" className="supplier-pitch__status-action">
                    <img src={editProfileInfo} alt="" onClick={() => setActiveStep(2)} />
                </button>
            </div>
        </section>
    );

    const goToParticipantsStep = () => {
        if (!hasSlots) return;
        setHasVisitedParticipantsStep(true);
        setActiveStep(2);
    };

    const slotsSectionActive = () => (
        <section key="slots" className="supplier-pitch__section">
            <div className="supplier-pitch__section-header">
                <span className="supplier-pitch__section-number">1</span>
                <h2 className="supplier-pitch__section-title">Select Your Preferred Pitch Slots</h2>
            </div>

            <p className="new-pitch-slots-proposed__picker-label">Please select one of the available options</p>
            {hasSlots ? (
                <div className="new-pitch-slots-proposed__slots" role="radiogroup" aria-label="Pitch slot options">
                    {slots.map((slot: any, index: number) => {
                        const selected = index === selectedProposedSlotIndex;
                        return (
                            <div
                                key={`${slot.date}-${index}`}
                                role="radio"
                                aria-checked={selected}
                                tabIndex={0}
                                className={
                                    'new-pitch-slots-proposed__slot' +
                                    (selected ? ' new-pitch-slots-proposed__slot--selected' : '')
                                }
                                onClick={() => setSelectedProposedSlotIndex(index)}
                                onKeyDown={onSlotKeyDown(index)}
                            >
                                <div
                                    className={
                                        'new-pitch-slots-proposed__slot-left' +
                                        (selected ? ' new-pitch-slots-proposed__slot-left--selected' : '')
                                    }
                                >
                                    <span
                                        className="new-pitch-slots-proposed__radio"
                                        aria-hidden
                                        data-checked={selected}
                                    />
                                    <span className="new-pitch-slots-proposed__slot-name">{slot.date}</span>
                                </div>
                                <div className="new-pitch-slots-proposed__slot-right">
                                    <span className="new-pitch-slots-proposed__meta">
                                        <CalendarSvg fill="#001965" className="" />
                                        <span className="new-pitch-slots-proposed__meta-text">{slot.time_from}</span>
                                    </span>
                                    <span className="new-pitch-slots-proposed__meta">
                                        <ClockIcon fill="#001965" />
                                        <span className="new-pitch-slots-proposed__meta-text">{slot.time_to}</span>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="new-pitch-slots-proposed__empty" role="status">
                    No alternative slots are available at the moment. You can request new slots or withdraw from the
                    process.
                </p>
            )}
            <div className="supplier-pitch__btns">
                <Button type="default" className="supplier-pitch__btn" onClick={() => setRequestNewSlotModal(true)}>
                    Request New Slots
                </Button>
                <Button className="supplier-pitch__btn quit" onClick={() => setWithDrawModal(true)}>
                    Withdraw from Process
                </Button>
                <Button
                    className="supplier-pitch__btn blue-btn"
                    type="primary"
                    onClick={goToParticipantsStep}
                    disabled={!hasSlots}
                >
                    Next
                </Button>
            </div>
        </section>
    );

    const slotsSectionCompleted = () => (
        <section key="slots" className="supplier-pitch__section">
            <div className="supplier-pitch__section-header">
                <span className="supplier-pitch__section-number inactive">1</span>
                <h2 className="supplier-pitch__section-title">Select Your Preferred Pitch Slots</h2>
            </div>
            <div className="supplier-pitch__status-bar">
                <div className="supplier-pitch__status-info">
                    <span className="supplier-pitch__status-icon">{statusCheckIcon}</span>
                    <span className="supplier-pitch__status-text">Slot selected</span>
                </div>
                <button type="button" className="supplier-pitch__status-action">
                    <img src={editProfileInfo} alt="" onClick={() => setActiveStep(1)} />
                </button>
            </div>
        </section>
    );

    return (
        <>
            {isSuccessWithdraw ? (
                <ParticipationWithdrawnSuccess />
            ) : isSuccess ? (
                newSlot ? (
                    <RequsetNewSlotSuccess />
                ) : (
                    <PitchSlotConfirmedSuccess
                        dateLabel={pitchConfirmedDateLabel}
                        timeRange={pitchConfirmedTimeRange}
                    />
                )
            ) : newSlot || withdrawReason ? (
                <div className="SupplierCompanyPitches__main">
                    <div className="supplier-pitch">
                        <div className="supplier-pitch__content new-pitch-slots-proposed">
                            <div className="new-pitch-slots-proposed__stack">
                                <div className="new-pitch-slots-proposed__upper">
                                    <div className="supplier-pitch__wrapper new-pitch-slots-proposed__hero">
                                        <img
                                            src={PitchFormImage}
                                            alt=""
                                            className="SupplierCompanyPitches__image-mobile"
                                        />
                                        <div className="new-pitch-slots-proposed__intro">
                                            <h1 className="new-pitch-slots-proposed__title">
                                                New Pitch Slots Proposed
                                            </h1>
                                            <p className="new-pitch-slots-proposed__lead">
                                                The Buyer has proposed the following alternative pitch slots for your
                                                selection
                                            </p>
                                        </div>
                                    </div>

                                    {newSlot ? (
                                        <>
                                            <div className="supplier-pitch__newSlot">
                                                <div className="supplier-pitch__newSlot-title">
                                                    {' '}
                                                    <div className="slot-value orange">
                                                        <CalendarSvg fill="#E64A0D" className={''} />
                                                        New Slot Requested
                                                    </div>
                                                    <Button
                                                        onClick={() => {
                                                            setNewSlot('');
                                                        }}
                                                    >
                                                        Cancel Request
                                                    </Button>
                                                </div>
                                                <div className="supplier-pitch__reason">
                                                    <h1>Comment</h1>
                                                    <div>{newSlot}</div>
                                                </div>
                                            </div>
                                            {participationSection()}
                                        </>
                                    ) : withdrawReason ? (
                                        <div className="supplier-pitch__newSlot">
                                            <div className="supplier-pitch__newSlot-title">
                                                {' '}
                                                <div className="slot-value error">
                                                    <img src={stopImage} alt="calendar" />
                                                    Participation Withdrawn
                                                </div>
                                                <Button
                                                    onClick={() => {
                                                        setWithdrawReason('');
                                                    }}
                                                >
                                                    Cancel Withdrawal
                                                </Button>
                                            </div>
                                            <div className="supplier-pitch__reason">
                                                <h1>Reason for Refusal</h1>
                                                <div>{withdrawReason}</div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>

                                <div className="supplier-pitch__btns">
                                    {!withdrawReason && (
                                        <Button
                                            type="default"
                                            className="supplier-pitch__btn"
                                            onClick={() => setWithDrawModal(true)}
                                        >
                                            Refus Participation
                                        </Button>
                                    )}
                                    <Button className="supplier-pitch__btn" type="primary" onClick={handleSubmit}>
                                        {isLoading || isLoadingWithdraw ? <Loader /> : 'Submit'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <img src={PitchFormImage} className="SupplierCompanyPitches__image" alt="" />
                </div>
            ) : (
                <div className="SupplierCompanyPitches__main">
                    <div className="supplier-pitch">
                        <div className="supplier-pitch__content new-pitch-slots-proposed">
                            <div className="new-pitch-slots-proposed__stack">
                                <div className="new-pitch-slots-proposed__upper">
                                    <div className="supplier-pitch__wrapper new-pitch-slots-proposed__hero">
                                        <img
                                            src={PitchFormImage}
                                            alt=""
                                            className="SupplierCompanyPitches__image-mobile"
                                        />
                                        <div className="new-pitch-slots-proposed__intro">
                                            <h1 className="new-pitch-slots-proposed__title">
                                                New Pitch Slots Proposed
                                            </h1>
                                            <p className="new-pitch-slots-proposed__lead">
                                                The Customer has proposed the following alternative pitch slots for the
                                                following service opportunity:
                                            </p>
                                            <div className="new-pitch-slots-proposed__tags">
                                                <span className="new-pitch-slots-proposed__tag">
                                                    {data?.company?.name}
                                                </span>

                                                <span className="new-pitch-slots-proposed__tag">
                                                    {data?.expense_area_name}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="new-pitch-slots-proposed__picker">
                                        {activeStep === 1 ? slotsSectionActive() : slotsSectionCompleted()}
                                        {activeStep === 2
                                            ? participationSection()
                                            : hasVisitedParticipantsStep && participantsValid
                                            ? participationSectionCompleted()
                                            : participationSectionPending()}
                                    </div>
                                </div>

                                {activeStep === 2 && (
                                    <div className="supplier-pitch__btns">
                                        <Button
                                            className="supplier-pitch__btn"
                                            onClick={() => handleSubmit()}
                                            type="primary"
                                            loading={isLoading}
                                        >
                                            Confirm Slot
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <img src={PitchFormImage} className="SupplierCompanyPitches__image" alt="" />
                </div>
            )}

            {requestNewSlotModal && (
                <RequestNewSlot
                    openModal={requestNewSlotModal}
                    setOpenModal={setRequestNewSlotModal}
                    requestNewSlot={(value) => setNewSlot(value)}
                />
            )}
            {withdrawModal && (
                <WithdrawModal
                    openModal={withdrawModal}
                    setOpenModal={setWithDrawModal}
                    requestWithdraw={(value) => {
                        setWithdrawReason(value);
                        setNewSlot('');
                        setWithDrawModal(false);
                    }}
                />
            )}
        </>
    );
};
